import React from "react";
import SeoHead from "../components/seo/SeoHead";
import { useTranslation } from "../i18n";
import "./AIHub.css";

const pageCopy = {
  es: {
    kicker: "TheQAHub AI Hub",
    title: "IA para QA, agentes y MCP",
    intro:
      "Esta seccion aterriza la conversacion actual sobre IA para testers: agentes, MCP, orquestacion, evaluacion y usos concretos en Quality Assurance. La idea no es vender humo, sino separar lo util, lo implementable y lo que de verdad cambia el trabajo diario de un equipo QA.",
    stats: [
      { value: "5", label: "roles de agente" },
      { value: "4", label: "patrones de orquestacion" },
      { value: "3", label: "bloques clave" },
    ],
    guideTitle: "Como se navega este mapa de IA para QA",
    blocks: [
      {
        title: "Agentes con roles claros",
        text:
          "En QA conviene distinguir muy bien quien piensa, quien ejecuta, quien revisa y quien coordina. Cuando separas esos papeles, la IA deja de parecer magia y empieza a parecer un sistema de trabajo entendible.",
      },
      {
        title: "MCP y contexto util",
        text:
          "La clave no es usar siglas, sino conectar el modelo con herramientas, documentacion, logs, pipelines y datos reales. Ahí es donde un agente empieza a aportar valor practico en testing.",
      },
      {
        title: "Automatizar con criterio",
        text:
          "La IA acelera discovery, triage y generacion de ideas, pero solo mejora un equipo cuando hay supervision, evaluacion continua, limites claros y una buena lectura del riesgo QA.",
      },
    ],
    agentTitle: "Mapa rapido de agentes",
    agentCards: [
      {
        name: "Assistant",
        intro: "Es la puerta de entrada mas rapida para explorar un dominio o desbloquear una tarea concreta.",
        points: [
          "Veras como resume bugs, reescribe casos de prueba y aclara dudas de forma inmediata.",
          "Entenderas cuando sirve como copiloto y cuando no deberias quedarte solo con su primera respuesta.",
        ],
      },
      {
        name: "Tool-using agent",
        intro: "Da el salto de conversar sobre el problema a actuar sobre sistemas y herramientas reales.",
        points: [
          "Veras ejemplos de llamadas a APIs, consulta de logs, lectura de CI y ejecucion de utilidades.",
          "Aprenderas por que las herramientas conectadas cambian por completo el valor del agente en QA.",
        ],
      },
      {
        name: "Planner",
        intro: "Sirve para bajar una peticion ambigua a un plan accionable, ordenado y defendible.",
        points: [
          "Veras como separa riesgos, escenarios, datos de prueba, automatizacion y seguimiento.",
          "Te ayudara a entender por que planificar bien ahorra ruido antes de automatizar o ejecutar.",
        ],
      },
      {
        name: "Reviewer",
        intro: "Es el rol que mas se parece al criterio QA cuando toca cuestionar si algo esta bien cubierto.",
        points: [
          "Veras como detecta huecos en coverage, acceptance criteria y suites demasiado optimistas.",
          "Te mostrara donde una respuesta parece correcta pero sigue dejando riesgo sin cubrir.",
        ],
      },
      {
        name: "Orchestrator",
        intro: "Coordina varias piezas y decide quien hace que, en que orden y con que nivel de supervision.",
        points: [
          "Veras handoffs, paralelizacion y decisiones de control entre agentes especializados.",
          "Entenderas cuando compensa montar un flujo multi-agent y cuando solo añade complejidad.",
        ],
      },
    ],
    patternTitle: "Patrones de orquestacion a vigilar",
    practiceTitle: "Como aterrizamos esto en QA",
    patterns: [
      "Single agent con herramientas: la opcion mas simple y la primera que conviene probar.",
      "Secuencial: un agente pasa su salida al siguiente, util para requisito -> casos -> automatizacion.",
      "Paralelo: varias especialidades trabajan a la vez, por ejemplo funcional, accesibilidad y rendimiento.",
      "Handoff o supervisor: un agente central decide a quien delegar segun contexto, riesgo y coste.",
    ],
    mcpTitle: "Lo importante de MCP para QA",
    mcpItems: [
      "Arquitectura host-client-server para separar aplicacion, conexion y capacidades.",
      "Primitivas claras: tools para acciones, resources para contexto y prompts para interacciones reutilizables.",
      "Buen encaje con laboratorios QA, copilotos internos, analisis de logs y asistentes conectados a sistemas reales.",
    ],
    qaTitle: "Aplicaciones reales de IA en QA",
    qaItems: [
      "Analizar historias de usuario y acceptance criteria para detectar ambiguedades o riesgos sin cubrir.",
      "Proponer escenarios, datos de prueba y particiones equivalentes antes de que exista automatizacion.",
      "Generar borradores de tests, page objects, mocks o consultas para observabilidad.",
      "Resumir fallos de CI, agrupar errores repetidos y acelerar el triage de regresiones.",
      "Evaluar productos con IA integrada usando criterios de calidad, seguridad, fiabilidad y sesgo.",
      "Mantener asistentes internos conectados a repositorios, pipelines o documentacion mediante MCP.",
    ],
    cautionTitle: "Lo que un QA no deberia delegar a ciegas",
    cautionItems: [
      "La priorizacion del riesgo de negocio.",
      "La validacion del resultado esperado cuando el dominio es ambiguo.",
      "La evaluacion de seguridad, privacidad o compliance sin controles extra.",
      "La confianza en respuestas generadas sin trazabilidad, fuentes o evaluacion.",
    ],
    playbookTitle: "Playbook para integrar IA en QA",
    playbookSteps: [
      {
        step: "01",
        title: "Empieza por una tarea estrecha",
        text: "Lo mas efectivo no suele ser pedirle todo a la IA. Empieza con una tarea concreta: analizar una historia, resumir un bug, proponer escenarios o revisar coverage.",
      },
      {
        step: "02",
        title: "Dale contexto util",
        text: "La diferencia entre una demo vistosa y una ayuda real está en el contexto: criterios de aceptacion, logs, documentacion, suites, datos de prueba y herramientas conectadas.",
      },
      {
        step: "03",
        title: "Mide donde aporta valor",
        text: "No basta con que responda rapido. Hay que observar si reduce ambiguedad, ahorra tiempo, destapa huecos o mejora la calidad de decisiones dentro del flujo QA.",
      },
      {
        step: "04",
        title: "Revisa y corrige",
        text: "Todo resultado generado necesita contraste. Lo importante no es si la IA escribe bonito, sino si el contenido aguanta una revision tecnica y cubre el riesgo correcto.",
      },
      {
        step: "05",
        title: "Escala solo cuando sea estable",
        text: "Cuando una tarea demuestra utilidad real, entonces tiene sentido convertirla en playbook, flujo con herramientas o patrón reutilizable dentro del equipo.",
      },
    ],
    evalTitle: "Como probar productos con IA sin quedarte en la superficie",
    evalPanels: [
      {
        title: "Que deberias evaluar",
        items: [
          "Calidad de respuesta: si la salida es correcta, util, consistente y alineada con el caso real.",
          "Robustez: como responde ante ambiguedad, ruido, inputs incompletos o intentos de desviar el objetivo.",
          "Seguridad y privacidad: si filtra datos, acepta instrucciones peligrosas o se deja manipular.",
          "Trazabilidad: si puedes entender de donde sale la respuesta y con que contexto ha trabajado.",
        ],
      },
      {
        title: "Fallos tipicos que un QA debe saber detectar",
        items: [
          "Alucinacion: inventa hechos, pasos o conclusiones que no estan en el contexto disponible.",
          "Grounding debil: mezcla informacion correcta con otra no verificada y la presenta con demasiada seguridad.",
          "Tool use defectuoso: llama una accion incorrecta, usa mal un parametro o interpreta mal una respuesta externa.",
          "Comportamiento inconsistente: la misma entrada produce estilos, decisiones o criterios distintos sin motivo claro.",
        ],
      },
    ],
    resourcesTitle: "Recursos oficiales para profundizar",
    resourceCta: "Abrir recurso",
    resources: [
      {
        label: "Model Context Protocol: Architecture",
        href: "https://modelcontextprotocol.io/specification/2025-06-18/architecture",
        intro: "La base tecnica para entender como se conectan app, cliente y servidor en un flujo MCP.",
        steps: [
          "Primero veras la arquitectura general host-client-server.",
          "Despues entenderas como se exponen capacidades y como circula el contexto.",
          "Al final podras aterrizarlo a copilotos QA, laboratorios internos o asistentes conectados.",
        ],
      },
      {
        label: "Model Context Protocol: Introduction",
        href: "https://modelcontextprotocol.io/introduction",
        intro: "Una entrada mucho mas amigable para entender MCP sin empezar por la especificacion.",
        steps: [
          "Primero veras el problema que MCP intenta resolver.",
          "Despues entenderas tools, resources y prompts con menos friccion conceptual.",
          "Al final te sera mas facil leer la arquitectura y pensar casos reales de QA.",
        ],
      },
      {
        label: "Microsoft: AI agent orchestration patterns",
        href: "https://learn.microsoft.com/en-us/azure/architecture/ai-ml/guide/ai-agent-design-patterns",
        intro: "Un marco muy util para no mezclar agentes, flujos y decisiones de orquestacion.",
        steps: [
          "Primero veras los patrones principales de coordinacion.",
          "Despues compararas secuencial, paralelo, supervisor y handoff.",
          "Al final podras decidir que patron encaja mejor en un flujo QA real.",
        ],
      },
      {
        label: "NIST: AI Test, Evaluation, Validation and Verification",
        href: "https://www.nist.gov/topic-terms/ai-measurement-and-evaluation",
        intro: "El marco mas serio de esta lista para pensar evaluacion de IA con rigor y no solo con intuicion.",
        steps: [
          "Primero veras el lenguaje de evaluacion y medicion que usa NIST.",
          "Despues entenderas por que TEVV importa cuando pruebas sistemas con IA.",
          "Al final tendras una base mejor para hablar de fiabilidad, seguridad y calidad.",
        ],
      },
      {
        label: "NIST GenAI evaluations",
        href: "https://ai-challenges.nist.gov/genai",
        intro: "Una forma de aterrizar la evaluacion en benchmarks, retos y comparaciones mas concretas.",
        steps: [
          "Primero veras como se plantean evaluaciones abiertas sobre GenAI.",
          "Despues entenderas que medir no es solo precision, sino robustez y comportamiento.",
          "Al final tendras ideas para montar evaluaciones mas serias en productos con IA.",
        ],
      },
    ],
  },
  en: {
    kicker: "TheQAHub AI Hub",
    title: "AI for QA, agents and MCP",
    intro:
      "This section turns the current AI conversation into something useful for testers: agents, MCP, orchestration, evaluation and concrete QA workflows. The goal is not hype. The goal is to separate what is practical, what is implementable and what really changes day-to-day QA work.",
    stats: [
      { value: "5", label: "agent roles" },
      { value: "4", label: "orchestration patterns" },
      { value: "3", label: "core blocks" },
    ],
    guideTitle: "How to navigate this AI map for QA",
    blocks: [
      {
        title: "Agents with clear roles",
        text:
          "In QA it helps to separate who thinks, who acts, who reviews and who coordinates. Once those roles are clear, AI stops feeling magical and starts feeling operational.",
      },
      {
        title: "MCP and useful context",
        text:
          "The point is not the acronym itself. The point is connecting models to tools, documentation, logs, pipelines and real data so the agent can operate with meaningful context.",
      },
      {
        title: "Automation with judgment",
        text:
          "AI speeds up discovery, triage and idea generation, but it only improves a QA team when there is supervision, ongoing evaluation, explicit limits and a strong risk mindset.",
      },
    ],
    agentTitle: "Quick agent map",
    agentCards: [
      {
        name: "Assistant",
        intro: "The fastest entry point when you need orientation, clarification or a first pass on a QA task.",
        points: [
          "You will see how it summarizes bugs, rewrites test cases and helps navigate documentation.",
          "You will also see where it is useful as a copilot and where its first answer should still be challenged.",
        ],
      },
      {
        name: "Tool-using agent",
        intro: "This is where the agent stops talking about work and starts interacting with real systems.",
        points: [
          "You will see API calls, log inspection, CI reading and concrete tool execution.",
          "You will understand why connected tools radically increase the practical value of an agent in QA.",
        ],
      },
      {
        name: "Planner",
        intro: "Useful when a request is broad and needs to become a clear, ordered and actionable plan.",
        points: [
          "You will see how it separates risks, scenarios, test data, automation work and follow-up.",
          "It helps explain why planning well removes noise before execution or automation.",
        ],
      },
      {
        name: "Reviewer",
        intro: "This role is closest to QA judgment when the real question is whether coverage is actually good enough.",
        points: [
          "You will see how it exposes weak coverage, missing acceptance criteria and optimistic suites.",
          "It helps reveal where an answer sounds correct but still leaves real risk uncovered.",
        ],
      },
      {
        name: "Orchestrator",
        intro: "Coordinates several moving parts and decides who should do what, when and under which controls.",
        points: [
          "You will see handoffs, parallel work and control decisions across specialized agents.",
          "It helps explain when multi-agent workflows are worth it and when they only add overhead.",
        ],
      },
    ],
    patternTitle: "Orchestration patterns worth tracking",
    practiceTitle: "How this becomes practical in QA",
    patterns: [
      "Single agent with tools: usually the simplest option and the first one worth trying.",
      "Sequential flow: one agent feeds the next, useful for requirement -> test cases -> automation.",
      "Parallel flow: several specialties work at once, such as functional, accessibility and performance.",
      "Handoff or supervisor: a central agent decides where to delegate based on context, risk and cost.",
    ],
    mcpTitle: "What matters about MCP for QA",
    mcpItems: [
      "A host-client-server architecture that separates the app, the connection layer and the exposed capabilities.",
      "Clear primitives: tools for actions, resources for context and prompts for reusable interactions.",
      "A strong fit for QA labs, internal copilots, log analysis and assistants connected to real systems.",
    ],
    qaTitle: "Real AI applications in QA",
    qaItems: [
      "Analyze user stories and acceptance criteria to uncover ambiguity or missing risk coverage.",
      "Propose scenarios, test data and equivalence partitions before automation exists.",
      "Generate first drafts of tests, page objects, mocks or observability queries.",
      "Summarize CI failures, cluster repeated errors and speed up regression triage.",
      "Evaluate AI-enabled products with quality, safety, reliability and bias criteria.",
      "Run internal assistants connected to repositories, pipelines or documentation through MCP.",
    ],
    cautionTitle: "What a QA should not delegate blindly",
    cautionItems: [
      "Business risk prioritization.",
      "Expected-result validation when the domain is ambiguous.",
      "Security, privacy or compliance assessment without extra controls.",
      "Trust in generated answers without traceability, sources or evaluation.",
    ],
    playbookTitle: "Playbook for integrating AI into QA",
    playbookSteps: [
      {
        step: "01",
        title: "Start with a narrow task",
        text: "The best first move is rarely asking AI to do everything. Start with something bounded: analyze a story, summarize a bug, propose scenarios or review coverage.",
      },
      {
        step: "02",
        title: "Give it useful context",
        text: "The difference between a flashy demo and a real helper is context: acceptance criteria, logs, docs, suites, test data and connected tools.",
      },
      {
        step: "03",
        title: "Measure where it adds value",
        text: "Speed alone is not enough. Watch whether it reduces ambiguity, saves time, exposes gaps or improves the quality of QA decisions.",
      },
      {
        step: "04",
        title: "Review and challenge",
        text: "Every generated output needs verification. What matters is not whether the answer sounds polished, but whether it survives technical review and covers the right risk.",
      },
      {
        step: "05",
        title: "Scale only when stable",
        text: "Once a task proves useful in practice, then it makes sense to turn it into a playbook, a tool-connected workflow or a reusable team pattern.",
      },
    ],
    evalTitle: "How to test AI products without staying on the surface",
    evalPanels: [
      {
        title: "What you should evaluate",
        items: [
          "Response quality: whether the output is correct, useful, consistent and aligned with the real use case.",
          "Robustness: how it behaves under ambiguity, noise, incomplete input or attempts to derail the objective.",
          "Safety and privacy: whether it leaks data, accepts unsafe instructions or can be manipulated too easily.",
          "Traceability: whether you can understand where the answer came from and what context was used.",
        ],
      },
      {
        title: "Failure modes a QA should recognize",
        items: [
          "Hallucination: it invents facts, steps or conclusions that are not grounded in the available context.",
          "Weak grounding: it blends correct and unverified information and presents both with too much confidence.",
          "Broken tool use: it calls the wrong action, passes bad parameters or misreads an external response.",
          "Inconsistent behavior: the same input produces different styles, decisions or criteria without a clear reason.",
        ],
      },
    ],
    resourcesTitle: "Official resources to go deeper",
    resourceCta: "Open resource",
    resources: [
      {
        label: "Model Context Protocol: Architecture",
        href: "https://modelcontextprotocol.io/specification/2025-06-18/architecture",
        intro: "The technical foundation for understanding how app, client and server connect in an MCP flow.",
        steps: [
          "First you will see the host-client-server architecture.",
          "Then you will understand how capabilities are exposed and how context moves across the system.",
          "By the end you can map it to QA copilots, internal labs or connected assistants.",
        ],
      },
      {
        label: "Model Context Protocol: Introduction",
        href: "https://modelcontextprotocol.io/introduction",
        intro: "A friendlier entry point if you want to understand MCP before reading the full specification.",
        steps: [
          "First you will see the problem MCP is trying to solve.",
          "Then you will understand tools, resources and prompts with less conceptual friction.",
          "By the end the architecture page will feel much easier to digest.",
        ],
      },
      {
        label: "Microsoft: AI agent orchestration patterns",
        href: "https://learn.microsoft.com/en-us/azure/architecture/ai-ml/guide/ai-agent-design-patterns",
        intro: "A practical framework for separating agent roles, workflow shapes and orchestration choices.",
        steps: [
          "First you will see the main coordination patterns.",
          "Then you will compare sequential, parallel, supervisor and handoff models.",
          "By the end you can choose a pattern with more confidence for real QA workflows.",
        ],
      },
      {
        label: "NIST: AI Test, Evaluation, Validation and Verification",
        href: "https://www.nist.gov/topic-terms/ai-measurement-and-evaluation",
        intro: "The most rigorous reference here if you want to talk about AI evaluation without hand-waving.",
        steps: [
          "First you will see the evaluation and measurement framing used by NIST.",
          "Then you will understand why TEVV matters when testing AI-enabled systems.",
          "By the end you will have a stronger base for reliability, safety and quality conversations.",
        ],
      },
      {
        label: "NIST GenAI evaluations",
        href: "https://ai-challenges.nist.gov/genai",
        intro: "A more concrete way to connect evaluation with benchmarks, challenges and measurable behavior.",
        steps: [
          "First you will see how open GenAI evaluations are structured.",
          "Then you will understand why measuring is not just about accuracy, but also robustness and behavior.",
          "By the end you will have ideas for more serious QA evaluation loops around AI products.",
        ],
      },
    ],
  },
  de: {
    kicker: "TheQAHub AI Hub",
    title: "KI fur QA, Agents und MCP",
    intro:
      "Dieser Bereich macht die aktuelle KI-Diskussion fur Tester nutzlich: Agents, MCP, Orchestrierung, Evaluation und konkrete QA-Anwendungen. Es geht nicht um Hype, sondern um das, was praktikabel, umsetzbar und im QA-Alltag wirklich wertvoll ist.",
    stats: [
      { value: "5", label: "Agent-Rollen" },
      { value: "4", label: "Orchestrierungsmuster" },
      { value: "3", label: "Kernblöcke" },
    ],
    guideTitle: "So navigierst du diese KI-Karte fur QA",
    blocks: [
      {
        title: "Agents mit klaren Rollen",
        text:
          "In QA hilft es, sauber zu trennen, wer denkt, wer handelt, wer reviewt und wer koordiniert. Wenn diese Rollen klar sind, wirkt KI weniger wie Magie und mehr wie ein echtes Arbeitssystem.",
      },
      {
        title: "MCP und nutzlicher Kontext",
        text:
          "Wichtig ist nicht die Abkurzung selbst, sondern dass Modelle mit Tools, Dokumentation, Logs, Pipelines und echten Daten verbunden werden können.",
      },
      {
        title: "Automatisierung mit Augenmass",
        text:
          "KI beschleunigt Discovery, Triage und Ideenfindung, verbessert ein QA-Team aber nur dann nachhaltig, wenn es Aufsicht, laufende Evaluation und eine starke Risikoperspektive gibt.",
      },
    ],
    agentTitle: "Schnelle Agent-Landkarte",
    agentCards: [
      {
        name: "Assistant",
        intro: "Der schnellste Einstieg, wenn du Orientierung, Klarheit oder einen ersten QA-Entwurf brauchst.",
        points: [
          "Du wirst sehen, wie er Bugs zusammenfasst, Testfalle uberarbeitet und bei Dokumentation hilft.",
          "Du verstehst auch, wann er als Copilot reicht und wann seine erste Antwort nicht genug ist.",
        ],
      },
      {
        name: "Tool-using agent",
        intro: "Hier geht der Agent vom Reden uber Arbeit zum Handeln auf echten Systemen uber.",
        points: [
          "Du wirst API-Aufrufe, Log-Analyse, CI-Auswertung und konkrete Tool-Nutzung sehen.",
          "Du verstehst, warum verbundene Tools den praktischen Wert eines Agents fur QA stark erhohen.",
        ],
      },
      {
        name: "Planner",
        intro: "Nutzlich, wenn aus einer breiten Anfrage ein klarer und umsetzbarer Plan werden soll.",
        points: [
          "Du wirst sehen, wie Risiken, Szenarien, Testdaten, Automatisierung und Nachverfolgung getrennt werden.",
          "Das hilft zu verstehen, warum gutes Planen Rauschen vor Ausfuhrung und Automatisierung reduziert.",
        ],
      },
      {
        name: "Reviewer",
        intro: "Diese Rolle kommt echtem QA-Urteil am nachsten, wenn es um ausreichende Abdeckung geht.",
        points: [
          "Du wirst sehen, wie schwache Coverage, fehlende Akzeptanzkriterien und zu optimistische Suites sichtbar werden.",
          "Sie zeigt, wo etwas korrekt klingt, aber dennoch relevantes Risiko offen lasst.",
        ],
      },
      {
        name: "Orchestrator",
        intro: "Koordiniert mehrere Teile und entscheidet, wer was wann und unter welchen Kontrollen macht.",
        points: [
          "Du wirst Handoffs, Parallelisierung und Steuerungsentscheidungen zwischen spezialisierten Agents sehen.",
          "So wird klarer, wann Multi-Agent wirklich hilft und wann es nur Komplexitat erzeugt.",
        ],
      },
    ],
    patternTitle: "Wichtige Orchestrierungsmuster",
    practiceTitle: "Wie das in QA praktisch wird",
    patterns: [
      "Single agent mit Tools: meist die einfachste und erste sinnvolle Option.",
      "Sequenziell: ein Agent gibt an den nachsten weiter, nutzlich fur Anforderung -> Testfalle -> Automatisierung.",
      "Parallel: mehrere Spezialisierungen arbeiten gleichzeitig, etwa funktional, Accessibility und Performance.",
      "Handoff oder Supervisor: ein zentraler Agent delegiert je nach Kontext, Risiko und Kosten.",
    ],
    mcpTitle: "Was bei MCP fur QA wichtig ist",
    mcpItems: [
      "Eine Host-Client-Server-Architektur trennt Anwendung, Verbindungslogik und Fahigkeiten.",
      "Klare Primitiven: tools fur Aktionen, resources fur Kontext und prompts fur wiederverwendbare Interaktionen.",
      "Gute Basis fur QA-Labs, interne Copilots, Log-Analyse und Assistenten mit Zugriff auf reale Systeme.",
    ],
    qaTitle: "Reale KI-Anwendungen im QA",
    qaItems: [
      "User Stories und Akzeptanzkriterien auf Mehrdeutigkeit oder fehlende Risiken analysieren.",
      "Szenarien, Testdaten und Aquivalenzklassen vorschlagen, bevor Automatisierung existiert.",
      "Erstentwurfe fur Tests, Page Objects, Mocks oder Observability-Abfragen erzeugen.",
      "CI-Fehler zusammenfassen, wiederholte Fehler gruppieren und Regression-Triage beschleunigen.",
      "KI-Produkte mit Blick auf Qualitat, Sicherheit, Zuverlassigkeit und Bias bewerten.",
      "Interne Assistenten uber MCP mit Repositories, Pipelines oder Dokumentation verbinden.",
    ],
    cautionTitle: "Was QA nicht blind delegieren sollte",
    cautionItems: [
      "Priorisierung des Geschaftsrisikos.",
      "Validierung des erwarteten Ergebnisses bei mehrdeutigem Fachkontext.",
      "Sicherheits-, Datenschutz- oder Compliance-Bewertung ohne zusatzliche Kontrollen.",
      "Vertrauen in generierte Antworten ohne Nachvollziehbarkeit, Quellen oder Evaluation.",
    ],
    playbookTitle: "Playbook fur die Integration von KI in QA",
    playbookSteps: [
      {
        step: "01",
        title: "Mit einer engen Aufgabe starten",
        text: "Am Anfang sollte KI nicht alles auf einmal machen. Starte mit etwas klar Begrenztem: Story analysieren, Bug zusammenfassen, Szenarien vorschlagen oder Coverage reviewen.",
      },
      {
        step: "02",
        title: "Nutzlichen Kontext geben",
        text: "Der Unterschied zwischen Demo und echtem Nutzen liegt im Kontext: Akzeptanzkriterien, Logs, Doku, Suites, Testdaten und verbundene Tools.",
      },
      {
        step: "03",
        title: "Wertbeitrag messbar machen",
        text: "Geschwindigkeit allein reicht nicht. Beobachte, ob Mehrdeutigkeit sinkt, Zeit gespart wird, Lucken auftauchen oder QA-Entscheidungen besser werden.",
      },
      {
        step: "04",
        title: "Review und Gegencheck",
        text: "Jede generierte Ausgabe braucht Prufung. Entscheidend ist nicht, ob sie gut klingt, sondern ob sie einer technischen Review standhalt und das richtige Risiko abdeckt.",
      },
      {
        step: "05",
        title: "Nur stabil skalieren",
        text: "Wenn eine Aufgabe echten Nutzen zeigt, dann kann daraus ein Playbook, ein Tool-Workflow oder ein wiederverwendbares Muster im Team werden.",
      },
    ],
    evalTitle: "Wie man KI-Produkte testet, ohne an der Oberfläche zu bleiben",
    evalPanels: [
      {
        title: "Was du evaluieren solltest",
        items: [
          "Antwortqualität: ob die Ausgabe korrekt, nutzlich, konsistent und passend zum realen Anwendungsfall ist.",
          "Robustheit: wie sich das System bei Mehrdeutigkeit, Rauschen, unvollstandigen Inputs oder Ablenkungsversuchen verhalt.",
          "Sicherheit und Datenschutz: ob Daten leaken, riskante Anweisungen akzeptiert oder zu leicht manipuliert wird.",
          "Nachvollziehbarkeit: ob du verstehen kannst, woher die Antwort kommt und mit welchem Kontext gearbeitet wurde.",
        ],
      },
      {
        title: "Fehlermuster, die QA erkennen sollte",
        items: [
          "Halluzination: Fakten, Schritte oder Schlussfolgerungen werden erfunden, obwohl sie nicht im Kontext stehen.",
          "Schwaches Grounding: korrekte und unbestatigte Information werden vermischt und gleich sicher prasentiert.",
          "Fehlerhafte Tool-Nutzung: falsche Aktion, schlechte Parameter oder fehlerhafte Interpretation externer Antworten.",
          "Inkonsistentes Verhalten: dieselbe Eingabe fuhrt ohne klaren Grund zu unterschiedlichen Entscheidungen oder Stilen.",
        ],
      },
    ],
    resourcesTitle: "Offizielle Ressourcen zum Vertiefen",
    resourceCta: "Ressource offnen",
    resources: [
      {
        label: "Model Context Protocol: Architecture",
        href: "https://modelcontextprotocol.io/specification/2025-06-18/architecture",
        intro: "Die technische Grundlage, um zu verstehen, wie App, Client und Server in einem MCP-Flow zusammenspielen.",
        steps: [
          "Zuerst siehst du die Host-Client-Server-Architektur.",
          "Danach verstehst du, wie Fahigkeiten bereitgestellt werden und wie Kontext fliest.",
          "Am Ende kannst du das auf QA-Copilots, interne Labs oder verbundene Assistenten ubertragen.",
        ],
      },
      {
        label: "Model Context Protocol: Introduction",
        href: "https://modelcontextprotocol.io/introduction",
        intro: "Ein leichterer Einstieg, wenn du MCP verstehen willst, bevor du in die Spezifikation gehst.",
        steps: [
          "Zuerst siehst du, welches Problem MCP losen will.",
          "Danach verstehst du tools, resources und prompts mit weniger Reibung.",
          "Am Ende wird dir die Architekturseite deutlich leichter fallen.",
        ],
      },
      {
        label: "Microsoft: AI agent orchestration patterns",
        href: "https://learn.microsoft.com/en-us/azure/architecture/ai-ml/guide/ai-agent-design-patterns",
        intro: "Ein praktischer Rahmen, um Agent-Rollen, Workflow-Formen und Orchestrierung sauber zu trennen.",
        steps: [
          "Zuerst siehst du die wichtigsten Koordinationsmuster.",
          "Danach vergleichst du sequenzielle, parallele, Supervisor- und Handoff-Modelle.",
          "Am Ende kannst du ein Muster fur reale QA-Workflows sicherer auswahlen.",
        ],
      },
      {
        label: "NIST: AI Test, Evaluation, Validation and Verification",
        href: "https://www.nist.gov/topic-terms/ai-measurement-and-evaluation",
        intro: "Die fundierteste Referenz dieser Liste, wenn du uber KI-Evaluation mit echtem Anspruch sprechen willst.",
        steps: [
          "Zuerst siehst du das NIST-Verstandnis von Evaluation und Messung.",
          "Danach verstehst du, warum TEVV beim Testen KI-basierter Systeme wichtig ist.",
          "Am Ende hast du eine bessere Basis fur Zuverlassigkeit, Sicherheit und Qualitat.",
        ],
      },
      {
        label: "NIST GenAI evaluations",
        href: "https://ai-challenges.nist.gov/genai",
        intro: "Ein konkreterer Blick darauf, wie Evaluation uber Benchmarks, Challenges und Verhalten greifbar wird.",
        steps: [
          "Zuerst siehst du, wie offene GenAI-Evaluationen aufgebaut sind.",
          "Danach verstehst du, warum Messen mehr ist als reine Genauigkeit.",
          "Am Ende bekommst du Ideen fur ernsthaftere QA-Evaluationsschleifen bei KI-Produkten.",
        ],
      },
    ],
  },
};

function AIHub() {
  const { language } = useTranslation();
  const copy = pageCopy[language] || pageCopy.es;

  return (
    <div className="ai-hub-page">
      <SeoHead
        title={`${copy.title} | TheQAHub`}
        description={copy.intro}
        canonicalPath="/ai-hub"
      />

      <section className="ai-hub-hero">
        <div className="ai-hub-hero-copy">
          <p className="ai-hub-kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
          <p className="ai-hub-intro">{copy.intro}</p>
        </div>

        <div className="ai-hub-stats">
          {copy.stats.map((item) => (
            <article key={item.label} className="ai-hub-stat-card">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="ai-hub-section ai-hub-guide-section">
        <div className="ai-hub-section-heading">
          <p>{copy.guideTitle}</p>
        </div>

        <div className="ai-hub-block-grid">
        {copy.blocks.map((block) => (
          <article key={block.title} className="ai-hub-block-card">
            <h3>{block.title}</h3>
            <p>{block.text}</p>
          </article>
        ))}
        </div>
      </section>

      <section className="ai-hub-section">
        <div className="ai-hub-section-heading">
          <p>{copy.agentTitle}</p>
        </div>
        <div className="ai-hub-agent-grid">
          {copy.agentCards.map((card, index) => (
            <article key={card.name} className="ai-hub-agent-card">
              <span className="ai-hub-agent-index">{String(index + 1).padStart(2, "0")}</span>
              <h3>{card.name}</h3>
              <p className="ai-hub-agent-intro">{card.intro}</p>
              <ul className="ai-hub-agent-points">
                {card.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="ai-hub-section ai-hub-practice-section">
        <div className="ai-hub-section-heading">
          <p>{copy.practiceTitle}</p>
        </div>

        <div className="ai-hub-columns">
          <article className="ai-hub-panel">
            <h2>{copy.patternTitle}</h2>
            <ul>
              {copy.patterns.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="ai-hub-panel">
            <h2>{copy.mcpTitle}</h2>
            <ul>
              {copy.mcpItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>

        <div className="ai-hub-columns">
          <article className="ai-hub-panel">
            <h2>{copy.qaTitle}</h2>
            <ul>
              {copy.qaItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="ai-hub-panel ai-hub-panel-caution">
            <h2>{copy.cautionTitle}</h2>
            <ul>
              {copy.cautionItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="ai-hub-section ai-hub-playbook-section">
        <div className="ai-hub-section-heading">
          <p>{copy.playbookTitle}</p>
        </div>
        <div className="ai-hub-playbook-grid">
          {copy.playbookSteps.map((item) => (
            <article key={item.step} className="ai-hub-playbook-card">
              <span className="ai-hub-playbook-step">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ai-hub-section ai-hub-eval-section">
        <div className="ai-hub-section-heading">
          <p>{copy.evalTitle}</p>
        </div>
        <div className="ai-hub-columns">
          {copy.evalPanels.map((panel) => (
            <article key={panel.title} className="ai-hub-panel ai-hub-eval-panel">
              <h2>{panel.title}</h2>
              <ul>
                {panel.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="ai-hub-section ai-hub-resources">
        <div className="ai-hub-section-heading">
          <p>{copy.resourcesTitle}</p>
        </div>
        <div className="ai-hub-resource-grid">
          {copy.resources.map((resource) => (
            <a
              key={resource.href}
              className="ai-hub-resource-card"
              href={resource.href}
              target="_blank"
              rel="noreferrer"
            >
              <div className="ai-hub-resource-copy">
                <span>{resource.label}</span>
                <p>{resource.intro}</p>
                <ol className="ai-hub-resource-steps">
                  {resource.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
              <span className="ai-hub-resource-cta">{copy.resourceCta}</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AIHub;
